import {
  Button,
  ButtonGroup,
  Dialog,
  Input,
  Separator,
  Text,
  Flex,
} from '@/_components';
import { useContext, useState, type FC, type FormEvent } from 'react';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { type QuestionAnswerLaterDialogProps } from './QuestionAnswerLaterDialog.types';
import {
  getNDaysAfter,
  getIsPastDate,
  getInputFormattedDateForNDaysFromNow,
} from '@/_utils/dates';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';

const TODAYS_DATE: string = getNDaysAfter(0);

export const QuestionAnswerLaterDialog: FC<QuestionAnswerLaterDialogProps> = ({
  mode,
  isOpen,
  onCloseDialog,
  question,
}) => {
  const { selectedQuestionnaire, updateQuestion } = useContext(
    ClientQuestionnaireContext,
  );
  const { addToast } = useContext(ToastContext);
  const { updatePreviewQuestion } = useContext(PreviewContext);
  const { updateTemplateQuestion } = useContext(UserPreviewContext);

  const [answerByDate, setAnswerByDate] = useState(
    getInputFormattedDateForNDaysFromNow(7),
  );

  const isBeforeToday: boolean = !getIsPastDate(TODAYS_DATE, answerByDate);

  const validateAnswerByDate = () => {
    let error = '';
    const answerByDateAsDate = new Date(answerByDate);

    if (answerByDateAsDate.getFullYear() !== new Date().getFullYear()) {
      error = 'Answer later date must be in the current year';
    }

    if (answerByDateAsDate < new Date()) {
      error = 'Answer later date must be in the future';
    }

    const due_date = selectedQuestionnaire?.due_date || '';

    if (answerByDateAsDate > new Date(due_date)) {
      error = `Answer later date must be before the due date: ${due_date}`;
    }

    if (error) {
      addToast('error', error);
    }

    return error;
  };

  const onAnswerLater = async (event: FormEvent) => {
    event.preventDefault();
    const error = validateAnswerByDate();

    if (error) return;

    const answerParams = {
      questionId: question.id,
      answer: '',
      answerBy: answerByDate,
    };

    try {
      if (mode === 'admin-preview') updatePreviewQuestion(answerParams);
      else if (mode === 'admin-template') updateTemplateQuestion(answerParams);
      else updateQuestion(answerParams);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      onCloseDialog();
    }
  };

  const onAnswerByChange = (event: any) => {
    setAnswerByDate(event.target.value);
  };

  return (
    <Dialog
      isOpen={isOpen}
      title="Answer question later"
      onClose={onCloseDialog}
    >
      <Dialog.Content>
        <form onSubmit={onAnswerLater}>
          <Flex isColumn={true} gap={10}>
            <Text>
              In case you need more time to answer this question, we&apos;ll
              send you a reminder on the selected date.
            </Text>
            <Text>
              Please note that all questions must be answered prior to
              submitting the questionnaire.
            </Text>
            <Input
              label="Answer Later Date"
              isLabelHidden={true}
              min={TODAYS_DATE}
              type="date"
              value={answerByDate}
              onChange={onAnswerByChange}
            />
            <Separator isLight={true} />
            <ButtonGroup align="center">
              <Button
                isDisabled={isBeforeToday}
                intent="primary"
                value="Schedule Reminder"
                type="submit"
              />
            </ButtonGroup>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog>
  );
};
